import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { AddDestinationAlternateModalComponent } from '@components/add-destination-alternate-modal/add-destination-alternate-modal.component';
import { AddDestinationPartialSuccessModalComponent } from '@components/add-destination-partial-success-modal/add-destination-partial-success-modal.component';
import { ImmutabilityNotDefinedComponent } from '@components/immutability-modals/immutability-not-defined/immutability-not-defined.component';
import { AzureOauthUrl } from '@models/azure-oauth.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import {
  AlertComponent,
  ButtonComponent,
  FormsUtil,
  MbsPopupType,
  MbsSize,
  ModalComponent,
  ModalFooterDirective,
  ModalService,
  ToastService
} from 'mbs-ui-kit';
import { StorageType } from 'mbs-ui-shared';
import { ftpStorageTypes, getBucketTypeName } from 'mbs-ui-shared@constants/storage.constants';
import { StorageDestinationsFacade } from 'mbs-ui-shared@facades/storage-destinations.facade';
import { BucketInfo, Destination, GoogleStorageClassEnum, SetDestinationType, StorageAccountV2 } from 'mbs-ui-shared@models/StorageAccount';
import { BehaviorSubject, from, noop, of } from 'rxjs';

import { AddDestinationFormComponent } from '@components/add-destination-form/add-destination-form.component';

import { CommonModule } from '@angular/common';
import { HelpLinkDirective } from 'mbs-ui-shared@help/helpLink.directive';
import { filter, finalize, switchMap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'mbs-add-destination-bucket-modal',
  templateUrl: './add-destination-modal.component.html',
  styleUrls: ['./add-destination-modal.component.scss'],
  host: {
    '(window:message)': 'handleMessage($event)'
  },
  imports: [
    AddDestinationFormComponent,
    AlertComponent,
    ButtonComponent,
    CommonModule,
    ModalComponent,
    I18NextPipe,
    HelpLinkDirective,
    ModalFooterDirective
  ]
})
export class AddDestinationModalComponent implements OnInit {
  private fb = inject(FormBuilder);
  private destinationsFacade = inject(StorageDestinationsFacade);
  private toast = inject(ToastService);
  private i18N = inject(I18NextPipe);
  private modalService = inject(ModalService);
  private ability = inject(AbilityService);

  public readonly popupType = MbsPopupType;
  public readonly storageTypeEnum = StorageType;
  public deprecatedStorageTypes = [StorageType.OracleCloud];

  public storageAccount: StorageAccountV2;
  public currentBucket: BucketInfo;
  public destination: Destination;
  public loading = true;
  public innerLoading = false;
  public form = this.fb.group({
    displayBucketName: new FormControl('', [Validators.required]),
    bucketName: new FormControl('', [Validators.required]),
    region: new FormControl(null),
    isGfsObjectLockEnabled: new FormControl(null),
    objectLockPeriodDays: new FormControl(null),
    storageClass: new FormControl(null),
    googleDestinationSubregion: new FormControl(null),
    companies: new FormControl([]),
    s3Accelerator: new FormControl(null),
    reuseAccountRules: new FormControl(null),
    isExternalBucket: new FormControl(null)
  });
  public finalSuccessAction: () => void;
  public createNew = new FormControl(true);
  public errorText = null;
  public bucketTypeName = null;
  public hasCriticalError = false;
  public editDestinationButtonLoading = new BehaviorSubject(false);

  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;

  handleMessage(event: MessageEvent): void {
    const { msg, data: credential } = event.data;

    if (msg === 'add-destination-modal') {
      this.applyAction(credential);
    }
  }

  ngOnInit() {
    if (this.deprecatedStorageTypes.includes(this.storageAccount.storageType)) {
      this.loading = false;
    }

    this.bucketTypeName = getBucketTypeName(this.storageAccount.storageType, this.i18N);
    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => !this.hasCriticalError && (this.errorText = null));

    this.form
      .get('isGfsObjectLockEnabled')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe((val) => console.log(val));
  }

  public getModalTitle(): string {
    return this.destination
      ? this.i18N.transform('shared:modals.addDestinationBucket.edit')
      : this.i18N.transform('shared:modals.addDestinationBucket.create');
  }

  public onActionButtonClick() {
    if (!this.form.valid) {
      return void FormsUtil.triggerValidation(this.form);
    }

    if (this.form.get('reuseAccountRules')?.value) {
      return void window.open(
        `${AzureOauthUrl}?action=add-destination-modal`,
        'Azure OAuth 2.0',
        'width=500,height=500,toolbar=0,menubar=0,location=0,status=1,scrollbars=1,resizable=1,left=0,top=0'
      );
    }

    this.applyAction();
  }

  public onCriticalError(error: string) {
    if (!error) return;

    this.hasCriticalError = true;
    this.errorText = error;
  }

  private handleFinalSuccess() {
    this.toast.success(this.i18N.transform('shared:modals.addDestinationBucket.destinationSaved'));
    this.finalSuccessAction && this.finalSuccessAction();
  }

  private editDestination(destinationId: Destination['id']) {
    this.editDestinationButtonLoading.next(true);
    this.destinationsFacade
      .getDestination(this.storageAccount.id, destinationId)
      .pipe(finalize(() => this.editDestinationButtonLoading.next(false)))
      .subscribe((destination) => {
        this.modalService.dismissAll();
        const modal = ftpStorageTypes.includes(this.storageAccount.storageType)
          ? AddDestinationAlternateModalComponent
          : AddDestinationModalComponent;

        const modalInstance = this.modalService.openRef(modal, {
          size: MbsSize.md
        });
        modalInstance.componentInstance.storageAccount = this.storageAccount;
        modalInstance.componentInstance.destination = destination;

        modalInstance.result
          .then(() => {
            this.finalSuccessAction && this.finalSuccessAction();
          })
          .catch(noop);
      });
  }

  private prepareData(credential?: { code: string; redirectUri: string }): SetDestinationType {
    const createNew = this.createNew.value;
    const formValue = this.form.value;
    const supportedStorageClassRegion =
      formValue?.storageClass?.id === GoogleStorageClassEnum.MultiRegional ? formValue?.region : formValue?.googleDestinationSubregion;

    const region = createNew
      ? this.storageAccount?.supportedStorageClass
        ? supportedStorageClassRegion
        : formValue.region
      : this.currentBucket?.region;

    return {
      displayBucketName: formValue.displayBucketName,
      isGfsObjectLockEnabled: formValue.isGfsObjectLockEnabled,
      objectLockPeriodDays: formValue.objectLockPeriodDays,
      createNewBucket: createNew,
      googleStorageClass: createNew ? formValue.storageClass?.id : null,
      assignToCompanies: formValue?.companies?.map((company) => company.id) || [],
      isExternalBucket: formValue.isExternalBucket,
      bucket: {
        bucketName: createNew ? formValue.bucketName : this.currentBucket?.bucketName,
        region,
        s3AcceleratorEnabled: formValue.s3Accelerator,
        cloudBucketID: createNew ? null : this.currentBucket?.cloudBucketID
      },
      credential
    };
  }

  private handlePartialSuccess(res: { extensions: { [key: string]: string }; instance: string; hasObjectLockWarning: boolean }) {
    this.baseModal.close();
    const modal = this.modalService.openRef(AddDestinationPartialSuccessModalComponent);
    modal.componentInstance.extensions = Object.values(res.extensions);
    modal.componentInstance.hasObjectLockWarning = res?.hasObjectLockWarning;

    modal.result
      .then((edit) => {
        edit && this.editDestination(res.instance);
      })
      .catch(() => {
        this.handleFinalSuccess();
      });
  }

  private applyAction(credential?: { code: string; redirectUri: string }) {
    this.innerLoading = true;

    const data: SetDestinationType = this.prepareData(credential);
    const olWasChanged =
      this.destination?.objectLockPeriodDays !== data.objectLockPeriodDays ||
      this?.destination?.isGfsObjectLockEnabled !== data.isGfsObjectLockEnabled;

    const preparation$ =
      this.currentBucket?.immutabilitySupported === null && olWasChanged
        ? from(this.modalService.openCustom(ImmutabilityNotDefinedComponent))
        : of(true);

    const query$ = this.destination
      ? this.destinationsFacade.editDestination({
          storageAccountId: this.storageAccount.id,
          data,
          destinationId: this.destination?.id,
          skipError: true
        })
      : this.destinationsFacade.createDestination({
          storageAccountId: this.storageAccount.id,
          data,
          skipError: true
        });

    preparation$
      .pipe(
        filter((res) => !!res),
        switchMap(() => query$),
        finalize(() => (this.innerLoading = false))
      )
      .subscribe({
        next: (res) => {
          if (res) {
            this.handlePartialSuccess(res);
          } else {
            this.handleFinalSuccess();
          }
          this.baseModal.save();
        },
        error: (err) => (this.errorText = err?.error?.detail)
      });
  }
}
